// TODO: SMT-1374 — add unit tests for buildActivitySummaryRequest
import { resolveRows } from './presets'
import { CALC_IDS } from './constants'

function extractDateParams ({ quickDate, startDate, endDate }) {
  if (quickDate) return { quickDate }
  return { startDate, endDate }
}

function buildMarketValueCalc (id, valuationMethod, shared) {
  const { reportCategoryId, inceptionValueMethod, includeAccruedInterest = false } = shared
  return {
    $type: 'activity',
    id,
    contextChange: {
      ...extractDateParams(shared),
      reportCategoryId,
      activityOption: {
        $type: 'market-value',
        valuationMethod,
        includeAccruedInterest,
        inceptionValueMethod
      }
    }
  }
}

function buildContextChangeEntry (row, inceptionValueMethod) {
  const details = row.activityTypes.map(type => ({
    $type: 'activity',
    activityType: type,
    isManaged: true,
    flipSign: row.flipSign ?? false
  }))

  return {
    activityOption: {
      $type: 'activity',
      name: row.name,
      inceptionValueMethod,
      includeUnmanagedJournals: false,
      details
    }
  }
}

function buildActivitySummaryCalc (activityRows, shared) {
  const { reportCategoryId, inceptionValueMethod } = shared
  const contextChanges = activityRows.map(row =>
    buildContextChangeEntry(row, inceptionValueMethod)
  )

  return {
    $type: 'grouping',
    id: CALC_IDS.ACTIVITY_SUMMARY,
    grouping: 'ActivityOptions',
    contextChange: {
      ...extractDateParams(shared),
      reportCategoryId
    },
    contextChanges,
    calculations: [
      {
        $type: 'activity',
        id: CALC_IDS.TRANSACTION_TOTAL,
        inceptionValueMethod
      }
    ]
  }
}

function buildPerformanceCalc (row, shared) {
  const { reportCategoryId } = shared
  return {
    $type: 'performance',
    id: `${CALC_IDS.PERFORMANCE_RETURN}_${row.method}`,
    contextChange: {
      ...extractDateParams(shared),
      reportCategoryId,
      performanceOption: {
        $type: 'performance',
        calculationMethod: row.method
      }
    }
  }
}

export default function buildActivitySummaryRequest (config, { entity, entityIds, asOfDate }) {
  const rows = resolveRows(config)
  const dateRange = config.dateRanges?.default ?? { quickDate: 'YTD' }
  const { reportCategoryId, inceptionValueMethod } = config

  const isCustom = dateRange.quickDate === 'Custom' && dateRange.startDate && dateRange.endDate
  const fallbackQuickDate = dateRange.quickDate && dateRange.quickDate !== 'Custom'
    ? dateRange.quickDate
    : 'YTD'
  const shared = isCustom
    ? { startDate: dateRange.startDate, endDate: dateRange.endDate, reportCategoryId, inceptionValueMethod }
    : { quickDate: fallbackQuickDate, reportCategoryId, inceptionValueMethod }

  const calculations = []

  // Always include BMV + EMV (needed for MV Change computation even if rows are hidden)
  calculations.push(
    buildMarketValueCalc(CALC_IDS.BEGINNING_MARKET_VALUE, 'Beginning Market Value', shared),
    buildMarketValueCalc(CALC_IDS.ENDING_MARKET_VALUE, 'Ending Market Value', shared)
  )

  // Bond accrual calculations: include if toggle is on OR any bond accrual row is enabled
  const hasBondAccrualRows = rows.some(r =>
    r.rowType === 'valuation' && r.includeAccruedInterest
  )
  if (config.bondAccrual || hasBondAccrualRows) {
    calculations.push(
      buildMarketValueCalc(
        CALC_IDS.BEGINNING_BOND_ACCRUAL,
        'Beginning Market Value',
        { ...shared, includeAccruedInterest: true }
      ),
      buildMarketValueCalc(
        CALC_IDS.ENDING_BOND_ACCRUAL,
        'Ending Market Value',
        { ...shared, includeAccruedInterest: true }
      )
    )
  }

  // Activity grouping from enabled activity rows
  const activityRows = rows.filter(r => r.rowType === 'activity')
  if (activityRows.length) {
    calculations.push(buildActivitySummaryCalc(activityRows, shared))
  }

  // Performance calculations from enabled performance rows
  const perfRows = rows.filter(r => r.rowType === 'performance')
  perfRows.forEach(row => {
    calculations.push(buildPerformanceCalc(row, shared))
  })

  return { entity, entityIds, asOfDate, calculations }
}
